import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@predoc/cow';
import { cn } from '@/lib/utils';
import type { FacesheetReference } from './types';

interface FacesheetReferencesSidebarProps {
  references: FacesheetReference[];
  activeReferenceId: string;
  onSelectReference: (referenceId: string) => void;
}

export const FacesheetReferencesSidebar = ({
  references,
  activeReferenceId,
  onSelectReference,
}: FacesheetReferencesSidebarProps) => {
  const activeIndex = references.findIndex(
    (ref) => ref.id === activeReferenceId,
  );
  const safeIndex = activeIndex >= 0 ? activeIndex : 0;
  const activeReference = references[safeIndex];

  const handlePrevious = () => {
    if (safeIndex > 0) {
      onSelectReference(references[safeIndex - 1].id);
    }
  };

  const handleNext = () => {
    if (safeIndex < references.length - 1) {
      onSelectReference(references[safeIndex + 1].id);
    }
  };

  return (
    <aside
      className="border-border bg-muted flex w-[355px] shrink-0 flex-col gap-2.5 border-l px-5 pt-5 pb-4"
      aria-label="References"
    >
      <div className="flex items-center gap-2.5">
        <div className="flex flex-1 items-end gap-2">
          <h2 className="text-foreground text-lg leading-none font-bold">
            References
          </h2>
          <span className="text-muted-foreground text-sm">
            {safeIndex + 1} of {references.length}
          </span>
        </div>
        <div className="flex items-center">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9 rounded-r-none"
            onClick={handlePrevious}
            disabled={safeIndex <= 0}
            aria-label="Previous reference"
          >
            <ChevronLeft className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="-ml-px size-9 rounded-l-none"
            onClick={handleNext}
            disabled={safeIndex >= references.length - 1}
            aria-label="Next reference"
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      <nav className="flex flex-col gap-1" aria-label="Reference list">
        {references.map((reference) => {
          const isSelected = reference.id === activeReference?.id;

          return (
            <Button
              key={reference.id}
              type="button"
              variant="ghost"
              onClick={() => onSelectReference(reference.id)}
              aria-current={isSelected ? 'true' : undefined}
              aria-label={reference.label}
              className={cn(
                'h-auto w-full justify-start rounded px-3 py-1.5 text-left text-sm font-medium transition-colors',
                isSelected
                  ? 'bg-background text-foreground hover:bg-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-transparent',
              )}
            >
              {reference.label}
            </Button>
          );
        })}
      </nav>
    </aside>
  );
};
